import { getSessionById, updateSessionFields } from '../../service/sessionService'

// Per-session settings authored in the creation wizard and the "Task and Unit Test" tab,
// and read by the student views. Kept in one module so the wizard, the instructor tab and
// the student runtime can never disagree about what a flag means.
//
// Backed by two hand-maintained columns (vizpi_backend/models/session.js):
//   enable_tests    BOOLEAN NOT NULL DEFAULT TRUE
//   ai_chat_policy  TEXT NULL

// Same cap the backend applies in gptService.studentChatCompletion.
export const AI_CHAT_POLICY_MAX = 2000

// --- test cases -------------------------------------------------------------------------

// `!== false` rather than truthiness: rows written before the column existed, and any
// backend that has not been migrated yet, have no enable_tests key at all - those must keep
// behaving exactly as they always have, i.e. with tests.
export const testsEnabled = (session) => session?.enable_tests !== false

// --- C++ and main() ---------------------------------------------------------------------
//
// A C++ exercise's generated harness has always been the only thing that defined main():
// the template is just the function under test. With test cases off there is no harness,
// so the template needs a main() for students to write their program in. The backend's
// runCpp is the safety net (it adds an empty main() or renames a clashing one), so these
// helpers are about giving students the right starting code, not about linking.

export const CPP_MAIN_TEMPLATE = 'int main() {\n    // Write your program here.\n    return 0;\n}\n'
export const cppDefinesMain = (source) => /\bint\s+main\s*\(/.test(source || '')

const cppTemplateForTests = (template, testsOn) => {
  if (testsOn) {
    // Only remove the stub this app inserted, and only while it is untouched - an
    // instructor-written main() is left alone (the backend renames it when tests run).
    return template === CPP_MAIN_TEMPLATE ? '' : template
  }
  if (cppDefinesMain(template)) return template
  return template.trim() ? `${template.replace(/\s+$/, '')}\n\n${CPP_MAIN_TEMPLATE}` : CPP_MAIN_TEMPLATE
}

// The test_code to write after the test toggle is set to `testsOn`, or null when nothing
// needs to change. Non-C++ sessions only ever need test_code to exist (so StarterCode
// seeds from '' instead of its '#StarterCode' placeholders).
export const testCodeForTestsSetting = (session, testsOn) => {
  const tc = session?.test_code
  const template = tc?.template ?? ''
  const nextTemplate = session?.type === 'CPP' ? cppTemplateForTests(template, testsOn) : template
  if (tc && nextTemplate === template) return null
  return { starter: tc?.starter ?? '', unit_test: tc?.unit_test ?? '', template: nextTemplate }
}

// --- AI chat ----------------------------------------------------------------------------
//
// session.enable_chat does NOT mean the same thing everywhere:
//   - Python / Vizmental (VizmentalIDE's AIChat) and every GUI type (Workspace's
//     ChatWindow): it is the student AI chat switch.
//   - CPP, SQL*, Blockly Shared, Helper/Helpee, Audio (StudentPanel's Chat / AudioChat):
//     it gates PEER chat. Those surfaces have no AI chat at all.
// So the wizard only ever writes it for the first group.
//
// And within the Python family, "AI chat on" is spelled with a different type name:
// 'Vizmental' is Python + AI chat. A bare 'Python' session with enable_chat=true is broken
// on /session/:id - Messages.jsx renders "Loading..." forever for any non-Vizmental type
// without group members, and AIChat reuses Messages - and the instructor's header AI switch,
// InteractionList and View Student Data are all Vizmental-only. So turning chat on for the
// Python family switches the type to 'Vizmental' too; that is the existing, proven path.

export const PYTHON_FAMILY = ['Python', 'Vizmental']
export const isPythonFamily = (type) => PYTHON_FAMILY.includes(type)

const GUI_CHAT_OPTIONAL = ['HTML', 'CSS', 'JavaScript', 'Mixed', 'JSBin']
// The Initial/Final comparison exercises are built around talking to the AI: the
// assignment tells students to describe the target to the assistant, and the compare
// layouts' right column contains nothing but the chat.
const GUI_CHAT_REQUIRED = ['InitialFinal', 'InitialFinalCSS', 'InitialFinalJS']

// 'optional' | 'required' | 'unavailable'
export const aiChatSupport = (type) => {
  if (isPythonFamily(type) || GUI_CHAT_OPTIONAL.includes(type)) return 'optional'
  if (GUI_CHAT_REQUIRED.includes(type)) return 'required'
  return 'unavailable'
}

export const aiChatEnabled = (session) => {
  if (!session) return false
  const support = aiChatSupport(session.type)
  if (support === 'unavailable') return false
  // A plain 'Python' row never has working AI chat, whatever enable_chat says (see above).
  if (session.type === 'Python') return false
  return Boolean(session.enable_chat)
}

// Whether a student gets no AI column at all (VizmentalIDE; the Session Setting preview
// mirrors it). AI chat off means the whole panel is gone, for Python and Vizmental alike.
// The column stays only while chat is on, or once the session has been ended
// (closeSessionType): End Session shows students the end-of-session survey in it.
export const hidesAiColumn = (session) =>
  Boolean(session) && !session.enable_chat && !session.closeSessionType

// Default for a type that has not been created yet, mirroring addSession's own
// enable_chat defaults (sessionService.js) so an untouched toggle reproduces them.
export const defaultAiChatFor = (type) => {
  if (type === 'Vizmental') return true
  if (type === 'Python') return false
  return aiChatSupport(type) !== 'unavailable'
}

// The fields to PUT for "AI chat on/off". Returns {} for types where the concept does not
// exist, so callers can never accidentally toggle peer chat.
//
// Off deliberately keeps the type: for a live Vizmental session, flipping it back to
// 'Python' would also reshuffle instructor tooling (Groups tab, header switch) mid-class.
// A Vizmental row with enable_chat=false is the same state the existing header switch
// produces.
export const buildAiChatUpdate = (type, on) => {
  const support = aiChatSupport(type)
  if (support === 'unavailable') return {}
  if (isPythonFamily(type)) {
    // closeSessionType is the end-of-session marker the header switch / End Session set.
    // A stale one would make AIChat show the end-of-session survey instead of the chat (or
    // the plain "disabled" notice), so this switch - which only shows or hides the
    // assistant - clears it.
    return on
      ? { type: 'Vizmental', enable_chat: true, closeSessionType: null }
      : { enable_chat: false, closeSessionType: null }
  }
  return { enable_chat: on }
}

// --- persistence ------------------------------------------------------------------------

// updateSessionFields never throws (it catches and returns false), and the backend's
// Sequelize model silently drops keys it has no column for while still answering 200 - the
// fate of the old ai_prompts field. So a write is only trusted after reading the row back
// and seeing every key we sent. `missing` non-empty means the backend has not been migrated
// (see vizpi_backend/postgre/migrations/2026-09-24-session-enable-tests-ai-chat-policy.sql).
export const saveSessionFields = async (sessionId, fields) => {
  const written = await updateSessionFields(sessionId, fields)
  if (!written) return { ok: false, session: null, missing: [], error: 'write-failed' }
  let fresh = null
  try {
    fresh = await getSessionById(sessionId)
  } catch (error) {
    return { ok: false, session: null, missing: [], error: 'read-back-failed' }
  }
  if (!fresh) return { ok: false, session: null, missing: [], error: 'read-back-failed' }
  const missing = Object.keys(fields).filter((key) => fresh[key] === undefined)
  return { ok: missing.length === 0, session: fresh, missing, error: missing.length ? 'not-migrated' : null }
}

export const describeSaveFailure = (result) => {
  if (!result || result.ok) return ''
  if (result.error === 'not-migrated') {
    return `The server did not store ${result.missing.join(', ')} - the backend database needs the ` +
      '2026-09-24 session migration before this setting can be saved.'
  }
  return 'Could not save this setting. Check your connection and try again.'
}
